import { LiveData, Store } from '@toeverything/infra';
import type { SuggestionServiceBeta } from '../services/suggestion-service-beta';
import type { LLMBaseDataSnapshot } from '../types';
import type { WorkspaceDBService } from '../../db';
import type { BaseSelection } from '@blocksuite/affine/store';

/**
 * AgentBetaStore - 只读视图层
 *
 * 职责：
 * - 提供 agent records 的只读内存视图
 * - 通过订阅 WorkspaceDB 自动同步数据
 * - 不直接修改数据，所有写操作通过 AgentStoreService
 *
 * 数据流：
 * WorkspaceDB (写入源) → 订阅 → AgentBetaStore (只读视图) → 提供访问接口
 */
export class AgentBetaStore extends Store {
  private readonly _originData$: LiveData<AgentBetaStoreModel>;
  // 临时放在这里维护
  private readonly _eventSelectionChanged$: LiveData<((event: any) => void)[]>;
  private _suggestionServiceIns: SuggestionServiceBeta | null;

  // chat config
  private readonly _chatConfig$: LiveData<ChatConfigType>;

  // 用于维护存在冲突未处理的文件
  private readonly _conflictDocIds$: LiveData<ConflictFileType[]>;

  // ✅ 数据库订阅的取消函数
  private _dbSubscription: (() => void) | null = null;

  constructor(private readonly workspaceDBService: WorkspaceDBService) {
    super();
    this._originData$ = new LiveData<AgentBetaStoreModel>({});
    this._eventSelectionChanged$ = new LiveData<((event: any) => void)[]>([]);
    this._suggestionServiceIns = null;

    this._conflictDocIds$ = new LiveData<ConflictFileType[]>([]);

    // 默认 doc 类型
    this._chatConfig$ = new LiveData<ChatConfigType>({
      type: 'doc',
      hasCreated: false,
    });

    // ✅ 订阅数据库变化
    this._subscribeToDatabase();

    // window.agentStore = this;
    (window as any).__agentStoreBeta = this;
  }

  /**
   * ✅ 订阅数据库变化，自动更新内存视图
   * 这是唯一更新 _originData$ 的地方
   *
   * ⚠️ 注意：订阅会在 DB 就绪后自动接收当前所有数据
   * find$() 是响应式的，会立即发送当前数据快照，然后持续监听变化
   */
  private _subscribeToDatabase() {
    try {
      // 使用 find$ 订阅所有 agentRecords 的变化
      // ✅ find$() 会立即发送当前数据，无需担心错过初始数据
      const subscription = this.workspaceDBService.db.agentRecords
        .find$()
        .subscribe(records => {
          const data: AgentBetaStoreModel = {};

          // 转换 DB 数据到内存格式
          records.forEach(record => {
            if (!data[record.doc_id]) {
              data[record.doc_id] = {
                doc_id: record.doc_id,
                records: [],
              };
            }

            // 每个 doc 只保留一条记录（与原有逻辑一致）
            if (data[record.doc_id].records.length === 0) {
              data[record.doc_id].records.push({
                docBaseDataSnapshot:
                  record.docBaseDataSnapshot as LLMBaseDataSnapshot,
                record_id: record.record_id,
                conversion_id: record.conversion_id,
                type: record.type as 'create' | 'update' | 'complex',
                commands: (record.commands as Array<any>) || [],
                title_new_value: record.title_new_value || undefined,
              });
            }
          });

          // 更新内存视图
          this._setData(data);
        });

      // 保存订阅以便清理
      this._dbSubscription = () => subscription.unsubscribe();
    } catch (error) {
      console.error('Failed to subscribe to database:', error);
    }
  }

  /**
   * ✅ 私有方法：只能从订阅中调用
   */
  private _setData(data: AgentBetaStoreModel) {
    this._originData$.next(data);
    // 检查是否存在冲突
    this._checkConflictDoc(data);
  }

  /**
   * ✅ 清理订阅
   */
  override dispose(): void {
    if (this._dbSubscription) {
      this._dbSubscription();
      this._dbSubscription = null;
    }
    super.dispose();
  }

  get data() {
    return this._originData$.getValue();
  }

  get selectionChangedSubscribe() {
    return this._eventSelectionChanged$.getValue();
  }

  private _checkConflictDoc(data: AgentBetaStoreModel) {
    const conflictDocs: ConflictFileType[] = [];

    Object.values(data).forEach(doc => {
      const records = doc.records.filter(item => {
        return item.commands.length > 0;
      });

      if (records.length >= 1) {
        conflictDocs.push({
          doc_id: doc.doc_id,
          options: {},
        });
      }
    });

    // 直接替换
    this._conflictDocIds$.next(conflictDocs);
  }

  /**
   * useage: conflictDocs.subscribe(cb) ｜ 监听存在冲突的文件
   */
  get conflictDocs$() {
    return this._conflictDocIds$;
  }

  // 维护 chat config
  get chatConfig$() {
    return this._chatConfig$;
  }

  /**
   * 设置 suggestion service 实例
   * 用于在 getEditorContent 中获取编辑器内容
   *
   * ⚠️ 注意：切换 doc 时会设置新的 service 实例
   * 旧实例应该已经被 dispose，这里只是更新引用
   */
  setSuggestionService(suggestionServiceIns: SuggestionServiceBeta) {
    // 记录切换日志，便于调试
    if (
      this._suggestionServiceIns &&
      this._suggestionServiceIns !== suggestionServiceIns
    ) {
      console.log('[AgentBetaStore] Switching suggestion service instance');
    }
    this._suggestionServiceIns = suggestionServiceIns;
  }

  setEditorDisplaySelections(selections: BaseSelection[]) {
    const sugIns = this._suggestionServiceIns;
    if (typeof sugIns === 'string') {
      return;
    }

    sugIns?.setEditorDisplaySelections(selections);
  }

  /**
   * 获取编辑器内容
   * ⚠️ Note: 该方法通过 suggestionService 代理获取，不直接操作编辑器
   */
  async getEditorContent() {
    const sugIns = this._suggestionServiceIns;
    if (typeof sugIns === 'string') {
      return [];
    }

    return sugIns?.getEditorContent().then(data => {
      const { docBaseDataSnapshot, ...rest } = data;

      // ⚠️ Note: Record creation is now handled by AgentStoreService
      // The caller should use agentStoreService.createRecordByDocId if needed

      return rest;
    });
  }

  /**
   * 订阅数据变化
   * 当 DB 中的 agent records 发生变化时，会自动触发回调
   */
  subscribe(cb: (data: AgentBetaStoreModel) => void) {
    this._originData$.subscribe(cb);
  }

  observeSelectionChanged(cb: (event: any) => void) {
    this._eventSelectionChanged$.next([
      ...this._eventSelectionChanged$.getValue(),
      cb,
    ]);
  }

  /**
   * ✅ 只读方法：获取指定 doc 的 record
   */
  getRecordByDocId(doc_id: string): LLMRecordType | null {
    const docModel = this.data[doc_id];
    if (!docModel) {
      return null;
    }
    // 先只维护一条 ｜ 最多只有一条
    return docModel.records[0];
  }

  /**
   * ⚠️ Deprecated: 请使用 AgentStoreService.updateCommands()
   * 保留此方法是为了在 updateCommands 时触发 suggestionService 处理
   */
  triggerSuggestionServiceCommand(params: {
    doc_id: string;
    conversion_id: string;
    commands: Array<any>;
    title_new_value?: string;
  }) {
    if (this._suggestionServiceIns) {
      // 内部负责检查
      this._suggestionServiceIns.processChatCommand({
        doc_id: params.doc_id,
        uuid: params.conversion_id,
        command: params.commands,
        title_new_value: params.title_new_value,
      });
    }
  }
}

type AgentBetaStoreModel = {
  [k: string]: DocModel;
};

type DocModel = {
  doc_id: string;
  // 先只维护一条
  records: Array<LLMRecordType>;
};

type LLMRecordType = {
  docBaseDataSnapshot: LLMBaseDataSnapshot;
  record_id: string;
  conversion_id: string;
  type: 'create' | 'update' | 'complex'; // complex 表示复杂的操作,不考虑模型操作类型只负责 diff markdown快照即可
  title_new_value?: string; // 标题的新值
  // 存储所有的 commands
  commands: Array<any>;
};

type ConflictFileType = {
  doc_id: string;
  options: {
    createdAt?: number;
    updatedAt?: number;
  };
};

export type ChatConfigType = {
  type: 'home' | 'doc';
  hasCreated: boolean;
};

export type ComplexRecordType = {
  snap_content: string; // 复杂操作的 markdown 快照
};
